import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { Button, Dropdown, Icon, Input, Loader } from "semantic-ui-react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";

import { t } from "../../../constant/translations";
import { amountToCurrency, getAppCurrency } from "../../../helpers/currency";
import { formatDate } from "../../../helpers/date";
import { fetchContract, fetchContracts } from "../../../redux/modules/contract";
import {
  createContractedService,
  deleteContractedService,
  fetchContractedServices,
  updateContractedService,
} from "../../../redux/modules/contracted-service";
import { ContractForm } from "./contract-form";
import BlockEdit from "../../block/edit";
import { ServiceCard } from "../../../components/management/contracts/service-card";
import { LinePanel } from "../../../components/management/contracts/line-panel";
import { isServiceActive } from "../../../helpers/contract-service";

// ─── Layout ────────────────────────────────────────────────────────────────

const PageWrap = styled.div`
  display: flex;
  height: calc(100vh - 90px);
  overflow: hidden;
  user-select: ${(props) => (props.dragging ? "none" : "auto")};
`;

const ListPanel = styled.div`
  width: ${(props) => props.width}px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  overflow: visible;
  background: #fafaf9;
`;

const ResizeDivider = styled.div`
  width: 1px;
  flex-shrink: 0;
  background: #e7e5e4;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ResizeHandle = styled.div`
  position: absolute;
  width: 16px;
  height: 32px;
  background: #fff;
  border: 1px solid #e7e5e4;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: col-resize;
  z-index: 10;
  transition: border-color 0.15s, box-shadow 0.15s;
  &:hover {
    border-color: #2185d0;
    box-shadow: 0 0 0 2px #e8f0fe;
  }
`;

const ListHeader = styled.div`
  padding: 20px 12px 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ListTitle = styled.h2`
  font-family: "Inter Tight", sans-serif;
  font-weight: 700;
  font-size: 20px;
  letter-spacing: -0.03em;
  color: #1c1917;
  margin: 0;
`;

const SearchWrap = styled.div`
  padding: 0 12px 8px;
`;

const SearchInput = styled(Input)`
  width: 100%;
  & input {
    border-radius: 6px !important;
    font-size: 13px !important;
  }
`;

const ActiveCount = styled.div`
  font-size: 12px;
  color: #78716c;
  padding: 6px 12px 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ContractList = styled.div`
  flex: 1;
  overflow-y: auto;
`;

const ContractRow = styled.div`
  padding: 14px 20px;
  cursor: pointer;
  border-left: 3px solid
    ${(props) => (props.selected ? "#2185d0" : "transparent")};
  background: ${(props) => (props.selected ? "#eff6ff" : "transparent")};
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  &:hover {
    background: ${(props) => (props.selected ? "#eff6ff" : "#f5f5f4")};
  }
`;

const ContractRowLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
`;

const BlockName = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: #1c1917;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const BlockAddress = styled.div`
  font-size: 12px;
  color: #78716c;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ContractMeta = styled.div`
  font-size: 12px;
  color: #a8a29e;
`;

const ContractRowRight = styled.div`
  display: ${(props) => (props.hidden ? "none" : "flex")};
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  flex-shrink: 0;
  margin-left: 12px;
`;

const ServiceTotal = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #1c1917;
`;

const ExpiryDate = styled.div`
  font-size: 12px;
  color: ${(props) => (props.expiring ? "#d97706" : "#a8a29e")};
`;

const SectionDivider = styled.div`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #a8a29e;
  padding: 12px 20px 4px;
`;

const FilterBar = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0 12px 20px;
  border-bottom: 1px solid #e7e5e4;
  overflow: visible;
  flex-wrap: nowrap;
  min-width: 0;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 4px;
  ${(props) =>
    props.$equal
      ? `
    flex: 1;
    & > .ui.dropdown { flex: 1; justify-content: center; }
  `
      : ""}
`;

const SortButton = styled(Dropdown)`
  flex-shrink: 0 !important;
  &.ui.dropdown {
    display: inline-flex !important;
    align-items: center !important;
    gap: 5px;
    padding: 5px 10px !important;
    border: 1px solid #e7e5e4;
    border-radius: 6px;
    color: #78716c;
    background: #fff;
    cursor: pointer;
    white-space: nowrap;
    line-height: 1 !important;
    min-height: 0 !important;
    & > .text {
      font-family: inherit;
      font-size: ${(props) => (props.$narrow ? "11px" : "13px")} !important;
      color: #78716c;
      line-height: 1 !important;
      order: 2;
    }
    & > i.icon {
      font-size: ${(props) => (props.$narrow ? "11px" : "13px")} !important;
      line-height: 1 !important;
      margin: 0 !important;
      height: auto !important;
      order: 1;
    }
    & > i.dropdown.icon {
      display: none !important;
    }
  }
`;

// ─── Detail Panel ──────────────────────────────────────────────────────────

const DetailPanel = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #fafaf9;
`;

const DetailHeader = styled.div`
  padding: 24px 32px 0;
  border-bottom: 1px solid #e7e5e4;
`;

const DetailHeaderTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
`;

const DetailTitle = styled.h1`
  font-family: "Inter Tight", sans-serif;
  font-weight: 700;
  font-size: 24px;
  letter-spacing: -0.03em;
  color: #1c1917;
  margin: 0;
`;

const DetailMeta = styled.div`
  font-size: 13px;
  color: #78716c;
  margin-bottom: 16px;
  display: flex;
  gap: 16px;
`;

const Tabs = styled.div`
  display: flex;
  gap: 24px;
`;

const Tab = styled.div`
  font-size: 15px;
  font-weight: ${(props) => (props.active ? "600" : "400")};
  color: ${(props) => (props.active ? "#1c1917" : "#78716c")};
  padding-bottom: 10px;
  border-bottom: 2px solid
    ${(props) => (props.active ? "#1c1917" : "transparent")};
  cursor: pointer;
`;

const DetailBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 32px;
`;

const CardsWrap = styled.div`
  padding: 12px 0 16px;
`;

const InactiveHeader = styled.div`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #a8a29e;
  padding: 22px 4px 4px;
`;

const AddSvc = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  color: #5b6068;
  padding: 16px 8px;
  font-size: 15px;
  cursor: pointer;
  border-radius: 10px;
  &:hover {
    background: #f6f9fe;
    color: #1c1917;
  }
  & .pl {
    width: 22px;
    height: 22px;
    border-radius: 6px;
    border: 1px dashed #9aa0a8;
    display: grid;
    place-items: center;
    color: #9aa0a8;
  }
`;

const DetailFooter = styled.div`
  padding: 16px 32px;
  border-top: 1px solid #e7e5e4;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TotalLabel = styled.span`
  font-size: 13px;
  color: #78716c;
  margin-right: 8px;
`;

const TotalAmount = styled.span`
  font-size: 22px;
  font-weight: 700;
  font-family: "Inter Tight", sans-serif;
  color: #1c1917;
`;

const DetailsContent = styled.div`
  padding: 20px 0;
`;

const NotesText = styled.p`
  font-size: 14px;
  color: #1c1917;
  line-height: 1.6;
  white-space: pre-wrap;
  margin: 0;
`;

const MutedText = styled.div`
  font-size: 14px;
  color: #a8a29e;
`;

const EmptyDetail = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #a8a29e;
  font-size: 15px;
`;

const MenuTrigger = styled.button`
  background: none;
  border: none;
  padding: 4px 6px;
  color: #a8a29e;
  cursor: pointer;
  border-radius: 4px;
  line-height: 1;
  display: flex;
  align-items: center;
  &:hover {
    background: #f5f5f4;
    color: #1c1917;
  }
  & .icon {
    margin: 0 !important;
  }
`;

const MenuWrap = styled.div`
  position: relative;
`;

const MenuPanel = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  background: #fff;
  border: 1px solid #e7e5e4;
  border-radius: 6px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  min-width: 160px;
  padding: 4px 0;
`;

const PopupMenuItem = styled.a`
  display: block;
  padding: 8px 16px;
  font-size: 14px;
  color: #1c1917;
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
  &:hover {
    background: #f5f5f4;
    color: #1c1917;
  }
`;

// ─── Helpers ────────────────────────────────────────────────────────────────

const TODAY = new Date();
const SIXTY_DAYS_MS = 60 * 24 * 60 * 60 * 1000;

function isExpiring(endDate) {
  if (!endDate) return false;
  const d = new Date(endDate);
  return d > TODAY && d - TODAY < SIXTY_DAYS_MS;
}

function isExpired(endDate) {
  if (!endDate) return false;
  return new Date(endDate) < TODAY;
}

function calcTotal(rows) {
  return rows
    .filter(isServiceActive)
    .reduce((sum, s) => sum + parseFloat(s.amount || 0), 0)
    .toFixed(2);
}

function matchesSearch(contract, q) {
  if (!q) return true;
  const lower = q.toLowerCase();
  return (
    (contract.block_name || "").toLowerCase().includes(lower) ||
    (contract.facility_manager_name || "").toLowerCase().includes(lower) ||
    (contract.fee_formula || "").toLowerCase().includes(lower)
  );
}

// ─── Component ───────────────────────────────────────────────────────────────

export const ContractsPage = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const selectedId = params.get("contract")
    ? parseInt(params.get("contract"), 10)
    : null;
  const lineId = params.get("line") ? parseInt(params.get("line"), 10) : null;
  const newLine = params.get("new") === "1";
  const searchQ = params.get("q") || "";
  const contracts = useSelector((state) => state.contract.entities);
  const contractsLoading = useSelector((state) => state.contract.pending);
  const serviceCategories = useSelector((state) =>
    state.appSettings.data
      ? state.appSettings.data.contracted_service_categories || []
      : []
  );
  const blocks = useSelector((state) => state.blocks.entities || []);
  const blocksPending = useSelector((state) => state.blocks.pending);
  const blocksError = useSelector((state) => state.blocks.error);
  const allServices = useSelector((state) => state.contractedService.entities);
  const services = allServices
    ? allServices.filter((s) => s.contract === selectedId)
    : null;
  const serviceError = useSelector(
    (state) => state.contractedService.error || null
  );

  const [activeTab, setActiveTab] = useState("services");
  const listFilter = params.get("filter") || "active";
  const listSort = params.get("sort") || "name";
  const [searchDisplay, setSearchDisplay] = useState(searchQ);
  const searchTimer = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [editModal, setEditModal] = useState(null);
  const [listWidth, setListWidth] = useState(350);
  const menuRef = useRef(null);
  const isDragging = useRef(false);
  const [dragging, setDragging] = useState(false);
  const [saving, setSaving] = useState(false);
  const [servicesLoadedFor, setServicesLoadedFor] = useState(null);

  const handleDividerMouseDown = (e) => {
    e.preventDefault();
    isDragging.current = true;
    setDragging(true);
    const startX = e.clientX;
    const startWidth = listWidth;
    const onMouseMove = (ev) => {
      if (!isDragging.current) return;
      const next = Math.min(
        350,
        Math.max(230, startWidth + ev.clientX - startX)
      );
      setListWidth(next);
    };
    const onMouseUp = () => {
      isDragging.current = false;
      setDragging(false);
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  useEffect(() => {
    dispatch(fetchContracts());
  }, []);

  useEffect(() => {
    if (!selectedId && contracts && contracts.length > 0) {
      const first =
        contracts.find((c) => c.block_active !== false) || contracts[0];
      const next = new URLSearchParams(location.search);
      next.set("contract", first.id);
      history.replace({ search: next.toString() });
    }
  }, [contracts]);

  useEffect(() => {
    if (selectedId) {
      setServicesLoadedFor(null);
      dispatch(fetchContractedServices(selectedId)).then(() => {
        setServicesLoadedFor(selectedId);
      });
    }
  }, [selectedId]);

  const selected = contracts && contracts.find((c) => c.id === selectedId);
  const selectedBlock = selected && blocks.find((b) => b.id === selected.block);

  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [menuOpen]);

  const blocksPendingRef = useRef(blocksPending);
  useEffect(() => {
    if (blocksPendingRef.current && !blocksPending && !blocksError) {
      dispatch(fetchContracts());
      if (selectedId) dispatch(fetchContract(selectedId));
    }
    blocksPendingRef.current = blocksPending;
  }, [blocksPending, blocksError]);

  const pushParams = (updates) => {
    const next = new URLSearchParams(location.search);
    Object.entries(updates).forEach(([k, v]) => {
      if (v == null || v === "") next.delete(k);
      else next.set(k, v);
    });
    history.push({ search: next.toString() });
  };

  const handleSelectContract = (contract) => {
    pushParams({ contract: contract.id, line: null, new: null });
    setActiveTab("services");
  };

  const handleSearch = (q) => {
    setSearchDisplay(q);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      pushParams({ q: q || null });
    }, 300);
  };

  const openLine = (id) => pushParams({ line: id, new: null });
  const openNew = () => pushParams({ line: null, new: "1" });
  const backToList = () => pushParams({ line: null, new: null });

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && (lineId || newLine)) backToList();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lineId, newLine]);

  const refreshAfterMutation = async () => {
    await dispatch(fetchContractedServices(selectedId));
    setServicesLoadedFor(selectedId);
    dispatch(fetchContract(selectedId));
  };

  const onCreate = async (f) => {
    setSaving(true);
    const res = await dispatch(
      createContractedService({
        contract: selectedId,
        name: f.name,
        category: f.category || "",
        amount: parseFloat(f.amount),
        start_date: f.start_date || null,
        end_date: f.end_date || null,
        notes: f.notes || "",
      })
    );
    setSaving(false);
    if (res && res.payload && res.payload.id) {
      await refreshAfterMutation();
      pushParams({ line: res.payload.id, new: null });
    } else if (res && res.data && res.data.id) {
      await refreshAfterMutation();
      pushParams({ line: res.data.id, new: null });
    } else {
      await refreshAfterMutation();
      backToList();
    }
  };

  const onUpdate = async (f) => {
    if (!lineId) return;
    setSaving(true);
    await dispatch(
      updateContractedService({
        id: lineId,
        contract: selectedId,
        name: f.name,
        category: f.category || "",
        amount: parseFloat(f.amount),
        start_date: f.start_date || null,
        end_date: f.end_date || null,
        notes: f.notes || "",
      })
    );
    setSaving(false);
    await refreshAfterMutation();
  };

  const onSchedule = async (ch) => {
    if (!lineId) return;
    const current = services && services.find((s) => s.id === lineId);
    if (!current) return;
    setSaving(true);
    await dispatch(
      updateContractedService({
        id: lineId,
        contract: selectedId,
        name: current.name,
        category: current.category || "",
        amount: current.amount,
        start_date: current.start_date || null,
        end_date: current.end_date || null,
        notes: current.notes || "",
        scheduled_change: ch,
      })
    );
    setSaving(false);
    await refreshAfterMutation();
  };

  const onDelete = async () => {
    if (!lineId) return;
    setSaving(true);
    await dispatch(deleteContractedService({ id: lineId }));
    setSaving(false);
    await refreshAfterMutation();
    backToList();
  };

  const onCancelScheduled = async () => {
    if (!lineId) return;
    const current = services && services.find((s) => s.id === lineId);
    if (!current) return;
    setSaving(true);
    await dispatch(
      updateContractedService({
        id: lineId,
        contract: selectedId,
        name: current.name,
        category: current.category || "",
        amount: current.amount,
        start_date: current.start_date || null,
        end_date: current.end_date || null,
        notes: current.notes || "",
        scheduled_change: null,
      })
    );
    setSaving(false);
    await refreshAfterMutation();
  };

  if (contractsLoading && !contracts) {
    return <Loader active />;
  }

  const contractList = contracts || [];
  const expiringCount = contractList.filter(
    (c) => c.block_active !== false && isExpiring(c.end_date)
  ).length;

  const sortContracts = (list) => {
    const sorted = [...list];
    if (listSort === "name") {
      sorted.sort((a, b) =>
        (a.block_name || "").localeCompare(b.block_name || "", "bg")
      );
    } else if (listSort === "total") {
      sorted.sort(
        (a, b) =>
          parseFloat(b.active_services_total || 0) -
          parseFloat(a.active_services_total || 0)
      );
    } else if (listSort === "end_date") {
      sorted.sort((a, b) => {
        if (!a.end_date && !b.end_date) return 0;
        if (!a.end_date) return 1;
        if (!b.end_date) return -1;
        return new Date(a.end_date) - new Date(b.end_date);
      });
    }
    return sorted;
  };

  const allActive = contractList.filter((c) => c.block_active !== false);
  const filteredActive = allActive.filter((c) => {
    if (!matchesSearch(c, searchDisplay)) return false;
    if (listFilter === "active") return !isExpired(c.end_date);
    if (listFilter === "expiring") return isExpiring(c.end_date);
    return true;
  });
  const activeContracts = sortContracts(filteredActive);
  const archivedContracts =
    listFilter === "all"
      ? sortContracts(
          contractList.filter(
            (c) => c.block_active === false && matchesSearch(c, searchDisplay)
          )
        )
      : [];

  const renderContractRow = (c) => {
    const expiring = isExpiring(c.end_date);
    const expired = isExpired(c.end_date);
    return (
      <ContractRow
        key={c.id}
        selected={c.id === selectedId}
        onClick={() => handleSelectContract(c)}
      >
        <ContractRowLeft>
          <BlockName>{c.block_name || c.block}</BlockName>
          {c.block_address && <BlockAddress>{c.block_address}</BlockAddress>}
          <ContractMeta>
            {[c.facility_manager_name, c.fee_formula]
              .filter(Boolean)
              .join(" · ")}
          </ContractMeta>
        </ContractRowLeft>
        <ContractRowRight hidden={listWidth < 280}>
          {parseFloat(c.active_services_total) > 0 && (
            <ServiceTotal>
              {amountToCurrency(
                c.active_services_total,
                getAppCurrency(),
                getAppCurrency()
              )}
            </ServiceTotal>
          )}
          <ExpiryDate expiring={expiring || expired}>
            {c.end_date
              ? `${t("contract.expires_label")} ${formatDate(c.end_date)}`
              : t("contract.no_end_date")}
          </ExpiryDate>
        </ContractRowRight>
      </ContractRow>
    );
  };

  const displayServices = services || [];
  const activeServices = displayServices.filter(isServiceActive);
  const inactiveServices = displayServices.filter((s) => !isServiceActive(s));
  const total = calcTotal(displayServices);
  const openService =
    lineId && services ? services.find((s) => s.id === lineId) : null;
  const inLineMode = newLine || lineId;

  return (
    <PageWrap dragging={dragging}>
      <ListPanel width={listWidth}>
        <ListHeader>
          <ListTitle>{t("contract.title")}</ListTitle>
        </ListHeader>
        <SearchWrap>
          <SearchInput
            icon="search"
            iconPosition="left"
            placeholder={t("contract.search_placeholder")}
            value={searchDisplay}
            onChange={(e) => handleSearch(e.target.value)}
            size="small"
          />
        </SearchWrap>
        <FilterBar>
          <div style={{ flex: 1 }} />
          <ButtonGroup $equal={listWidth < 320}>
            <SortButton
              $narrow={listWidth < 300}
              icon="filter"
              text={listWidth > 300 ? t("contract.filter_" + listFilter) : ""}
              options={[
                {
                  key: "active",
                  value: "active",
                  text: t("contract.filter_active"),
                },
                {
                  key: "expiring",
                  value: "expiring",
                  text:
                    expiringCount > 0
                      ? `${t("contract.filter_expiring")} (${expiringCount})`
                      : t("contract.filter_expiring"),
                },
                { key: "all", value: "all", text: t("contract.filter_all") },
              ]}
              onChange={(_, d) =>
                pushParams({ filter: d.value === "active" ? null : d.value })
              }
              value={listFilter}
            />
            <SortButton
              $narrow={listWidth < 300}
              icon="sort amount down"
              text={listWidth > 300 ? t("contract.sort_" + listSort) : ""}
              options={[
                { key: "name", value: "name", text: t("contract.sort_name") },
                {
                  key: "total",
                  value: "total",
                  text: t("contract.sort_total"),
                },
                {
                  key: "end_date",
                  value: "end_date",
                  text: t("contract.sort_end_date"),
                },
              ]}
              onChange={(_, d) =>
                pushParams({ sort: d.value === "name" ? null : d.value })
              }
              value={listSort}
            />
          </ButtonGroup>
        </FilterBar>
        <ActiveCount>
          {activeContracts.length} {t("contract.active_count")}
          {expiringCount > 0 &&
            ` · ${expiringCount} ${t(
              "contract.filter_expiring"
            ).toLowerCase()} скоро`}
        </ActiveCount>
        <ContractList>
          {activeContracts.map((c) => renderContractRow(c))}
          {archivedContracts.length > 0 && (
            <>
              <SectionDivider>{t("contract.archived")}</SectionDivider>
              {archivedContracts.map((c) => renderContractRow(c))}
            </>
          )}
        </ContractList>
      </ListPanel>
      <ResizeDivider>
        <ResizeHandle onMouseDown={handleDividerMouseDown}>
          <svg width="6" height="14" viewBox="0 0 6 14" fill="none">
            <line
              x1="1.5"
              y1="1"
              x2="1.5"
              y2="13"
              stroke="#a8a29e"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
            <line
              x1="4.5"
              y1="1"
              x2="4.5"
              y2="13"
              stroke="#a8a29e"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        </ResizeHandle>
      </ResizeDivider>

      {!selected ? (
        <DetailPanel>
          <EmptyDetail>{t("contract.select_prompt")}</EmptyDetail>
        </DetailPanel>
      ) : (
        <DetailPanel>
          <DetailHeader>
            <DetailHeaderTop>
              <DetailTitle>{selected.block_name || selected.block}</DetailTitle>
              <MenuWrap ref={menuRef}>
                <MenuTrigger onClick={() => setMenuOpen((o) => !o)}>
                  <Icon name="ellipsis vertical" />
                </MenuTrigger>
                {menuOpen && (
                  <MenuPanel>
                    {selectedBlock && (
                      <PopupMenuItem
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          setMenuOpen(false);
                          setEditModal("block");
                        }}
                      >
                        {t("contract.edit_block")}
                      </PopupMenuItem>
                    )}
                    <PopupMenuItem
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        setMenuOpen(false);
                        setEditModal("contract");
                      }}
                    >
                      {t("contract.edit_contract")}
                    </PopupMenuItem>
                  </MenuPanel>
                )}
              </MenuWrap>
              {selectedBlock && (
                <BlockEdit
                  entity={selectedBlock}
                  forceOpen={editModal === "block"}
                  onForceClose={() => setEditModal(null)}
                  trigger_props={{ style: { display: "none" } }}
                />
              )}
              <ContractForm
                entity={selected}
                forceOpen={editModal === "contract"}
                onForceClose={() => setEditModal(null)}
                trigger_props={{ style: { display: "none" } }}
              />
            </DetailHeaderTop>
            {!inLineMode && (
              <DetailMeta>
                {selected.facility_manager_name && (
                  <span>{selected.facility_manager_name}</span>
                )}
                {selected.start_date && (
                  <span>
                    {formatDate(selected.start_date)} —{" "}
                    {selected.end_date
                      ? formatDate(selected.end_date)
                      : t("contract.no_end_date")}
                  </span>
                )}
                {selected.fee_formula && <span>{selected.fee_formula}</span>}
              </DetailMeta>
            )}
            {!inLineMode && (
              <Tabs>
                <Tab
                  active={activeTab === "services"}
                  onClick={() => setActiveTab("services")}
                >
                  {t("contract.services_tab")}
                </Tab>
                <Tab
                  active={activeTab === "details"}
                  onClick={() => setActiveTab("details")}
                >
                  {t("contract.notes")}
                </Tab>
              </Tabs>
            )}
          </DetailHeader>

          {inLineMode ? (
            <LinePanel
              service={openService}
              isNew={!!newLine}
              categories={serviceCategories}
              saving={saving}
              onBack={backToList}
              onCreate={onCreate}
              onUpdate={onUpdate}
              onSchedule={onSchedule}
              onCancelScheduled={onCancelScheduled}
              onDelete={onDelete}
              error={serviceError}
            />
          ) : activeTab === "services" ? (
            <>
              <DetailBody>
                {servicesLoadedFor !== selectedId ? (
                  <Loader active />
                ) : (
                  <CardsWrap>
                    {activeServices.map((s) => (
                      <ServiceCard
                        key={s.id}
                        service={s}
                        onClick={() => openLine(s.id)}
                      />
                    ))}
                    <AddSvc onClick={openNew}>
                      <span className="pl">+</span>{" "}
                      {t("contract.line.add_service")}
                    </AddSvc>
                    {inactiveServices.length > 0 && (
                      <>
                        <InactiveHeader>
                          {t("contract.inactive_services")}
                        </InactiveHeader>
                        {inactiveServices.map((s) => (
                          <ServiceCard
                            key={s.id}
                            service={s}
                            onClick={() => openLine(s.id)}
                          />
                        ))}
                      </>
                    )}
                  </CardsWrap>
                )}
              </DetailBody>
              <DetailFooter>
                <div />
                <div>
                  <TotalLabel>{t("contract.subscription_total")}</TotalLabel>
                  <TotalAmount>
                    {amountToCurrency(
                      total,
                      getAppCurrency(),
                      getAppCurrency()
                    )}{" "}
                    {t("contract.per_month")}
                  </TotalAmount>
                </div>
              </DetailFooter>
            </>
          ) : (
            <DetailBody>
              <DetailsContent>
                {selected.notes ? (
                  <NotesText>{selected.notes}</NotesText>
                ) : (
                  <MutedText>{t("contract.no_notes")}</MutedText>
                )}
              </DetailsContent>
            </DetailBody>
          )}
        </DetailPanel>
      )}
    </PageWrap>
  );
};
